/**
 * AI聊天消息接口类型定义（SSE 版本）
 *
 * 接口地址前缀: /sse/*
 * 认证方式: Header (Chat-Token / Chat-Session)
 */

import { sseHttp } from "@/http/axios";

/** 消息内容类型 */
export type AIChatContentType = "text" | "audio";

/** 消息来源 */
export type AIChatSource = "user" | "ai" | "bot";

/** 历史消息中的单条数据 */
export interface HistoryMessage {
  id?: string;
  role?: string;
  content?: string;
  type?: string;
  msg_type?: string;
  action?: string;
  /**
   * 结构化数据（object）
   * - 查询类消息（msg_type=train/car/food/guide/flight/hotel/route/transfer）承载卡片数据
   * - 订单消息（action=book）承载订单字段
   * - 少数场景后端可能返回 JSON 字符串，读取时需兼容解析
   */
  data?: Record<string, any> | string | null;
  /** 订单消息专用：订单详情（优先级高于 data） */
  __data?: Record<string, any> | null;
  /** 历史消息中的展示文本（优先级高于 content） */
  display_content?: string;
  /** 纯文本字段（订单等场景后端用 message 承载） */
  message?: string;
  timestamp?: number;
  /** 服务端返回的角色标识（部分接口用） */
  sender?: { uid?: number; session_id?: string | null; name?: string; is_logged_in?: boolean };
  /** 支持字符串URL 或 ["data:audio/mp3;base64,", 数据] 数组格式，或 ["base64", 数据] 旧版数组格式 */
  audio_url?: string | [string, string];
}

/** AI聊天消息数据结构（兼容旧字段） */
export interface AIChatMessage {
  id: number;
  sender_id: number;
  receiver_id: number;
  content: string;
  type: AIChatContentType;
  msg_type: string;
  source: AIChatSource;
  created_at: number;
  audio_duration: number;
  read_at: number;
  audio_path: string;
  audio_url: string;
  nickname: string;
  avatar: string;
  is_guest: number;
  session_id: string;
}

/** AI聊天消息发送请求参数 */
export interface SendAIChatMessageParams {
  /** 消息类型：text-文本，audio-音频 */
  type: AIChatContentType;
  /** 消息内容：文本时传字符串，音频时传文件 */
  content: string | File | Blob;
  /** 会话ID（可选，通过 Header Chat-Session 已自动注入） */
  session_id?: string;
}

/**
 * 发送消息响应
 * POST /sse/send 返回:
 * { "success": true, "message_id": "msg_xxx", "hint": "消息已收到,AI回复将通过SSE推送" }
 */
export interface SendAIChatMessageResponse {
  success: boolean;
  message_id: string;
  hint?: string;
}

/** 获取历史消息列表请求参数 */
export interface FetchAIChatMessagesParams {
  /** 页码，默认1 */
  page?: string | number;
  /** 每页条数，默认20 */
  limit?: number;
  /**
   * 查询此时间戳之前的消息
   * 获取第一页时传当前时间戳，后续用 next_cursor
   * 默认使用当前时间戳
   */
  before?: string | number;
}

/** 获取历史消息响应 */
export interface FetchAIChatMessagesResponse {
  success: boolean;
  total: number;
  limit: number;
  has_next: boolean;
  has_prev: boolean;
  next_page: number | null;
  prev_page: number | null;
  first_time: number;
  last_time: number;
  messages: HistoryMessage[];
}

/** 绑定会话请求参数 */
export interface BindAIChatSessionParams {
  merge_strategy?: "append" | "replace";
}

/** 绑定会话响应中的身份信息 */
export interface BindIdentity {
  is_guest: boolean;
  uid: number;
  session_id: string | null;
  token: string;
  new_session: boolean;
}

/** 绑定会话响应 */
export interface BindAIChatSessionResponse {
  success: boolean;
  message: string;
  merged_count: number;
  new_identity: BindIdentity;
  identity_string: string;
}

// ════════════════════════════════════════════════════
//  接收SSE消息相关接口
// ════════════════════════════════════════════════════

/** SSE服务状态信息 */
export interface SseStatusInfo {
  service: string;
  version: string;
  status: string;
  sse_connections: number;
  redis_connected: boolean;
  ai_enabled: boolean;
  ai_info: {
    provider: string;
    model: string;
    streaming: boolean;
  };
  timestamp: string;
}

/** SSE连接身份信息 */
export interface SseConnectInfo {
  identity: {
    is_guest: boolean;
    uid: number | null;
    token: string | null;
    session_id: string;
    new_session: boolean;
  };
  identity_string: string;
  type: string;
  session_id: string;
  uid: number | null;
  timestamp: number;
  server_time: string;
}

/** 上线打招呼信息 */
export interface SseGreetingInfo {
  type: string;
  content: string;
  sender: {
    name: string;
    uid: number;
  };
  timestamp: number;
}

/** 普通消息 */
export interface SseNormalMessage {
  id: string;
  type: string;
  msg_type: string;
  action: string;
  content: string;
  data: any;
  sender: {
    uid: number;
    session_id: string | null;
    name: string;
    is_logged_in: boolean;
  };
  timestamp: number;
  metadata: {
    agent: string;
    streamed: boolean;
    tool_calls: number;
    model: string;
  };
}

/**
 * TTS 文字转语音消息
 *
 * audio_url 支持多种格式：
 * - 数组 ["data:audio/mp3;base64,", "base64编码数据"]（新版，直接拼接）
 * - 数组 ["base64", "base64编码数据"]（旧版，自动补 data:audio/mpeg;base64,）
 * - 字符串 URL（旧版兼容）
 */
export interface SseTtsMessage {
  type: "audio";
  msg_type: "tts_audio";
  action: "tts";
  content: string;
  data: {
    /** ["base64", 数据] 数组 或 字符串 URL */
    audio_url: [string, string] | string;
  };
}

/**
 * 将 audio_url（可能为数组或字符串）转换为可用的音频 URL
 *
 * 支持格式（后端可能调整）：
 * - ["base64", "数据"]        → "data:audio/mpeg;base64,数据"  （旧版）
 * - ["data:audio/mp3;base64,", "数据"] → "data:audio/mp3;base64,数据" （新版，直接拼接）
 * - "https://..."             → 原样返回
 * - undefined/null            → undefined
 */
export function resolveAudioUrl(raw: unknown): string | undefined {
  if (!raw) return undefined;
  if (Array.isArray(raw)) {
    // ★ 单元素数组：["data:audio/mp3;base64,完整数据"] → 直接取第一个元素
    if (raw.length === 1 && typeof raw[0] === "string" && raw[0]) {
      return raw[0];
    }
    if (raw.length >= 2 && typeof raw[0] === "string" && typeof raw[1] === "string" && raw[1]) {
      // 旧版格式：["base64", "数据"]
      if (raw[0] === "base64") {
        return `data:audio/mpeg;base64,${raw[1]}`;
      }
      // 新版格式：["data:audio/mp3;base64,", "数据"] → 直接拼接
      // ★ 防御：后端可能把 data URI 前缀同时放在两个数组元素中
      //    如 ["data:audio/mp3;base64,AAAA==", "data:audio/mp3;base64,BBBB"]
      //    直接拼接会导致中间出现两个前缀，atob 会因中间 == 而失败
      if (raw[0].includes("base64")) {
        let part2 = raw[1];
        const prefixIdx = part2.indexOf("base64,");
        if (prefixIdx !== -1) {
          part2 = part2.substring(prefixIdx + 7); // 跳过 "base64," 只保留纯 base64 数据
        }
        return raw[0] + part2;
      }
      // 未知数组格式：尝试直接使用第二个元素作为URL
      return raw[1];
    }
  }
  if (typeof raw === "string" && raw.length > 0) {
    return raw;
  }
  return undefined;
}

/** 通用查询消息（租车/美食/导游/机票/跳转页面等） */
export interface SseQueryMessage {
  id?: string;
  type: string;
  msg_type: string;
  action?: string;
  content: string;
  sender?: { uid?: number; session_id?: string | null; name?: string; is_logged_in?: boolean };
  timestamp?: number;
  data?: any;
  metadata?: { agent?: string; streamed?: boolean; tool_calls?: number; model?: string };
  [key: string]: any;
}

// ════════════════════════════════════════════════════
//  火车票查询专用类型
// ════════════════════════════════════════════════════

/** 火车票座位信息 */
export interface TrainSeatInfo {
  /** 二等座 */
  seat_ed: string;
  /** 一等座/硬卧 */
  seat_yd: string;
  /** 无座/商务座 */
  seat_wz: string;
  /** 软卧 */
  seat_yw: string;
  /** 硬座 */
  seat_yz: string;
}

/** 单趟火车信息 */
export type TrainItem = {
  /** 车次号 */
  train_no: string;
  /** 车型：高铁 / 动车 / 快速 */
  train_type: string;
  /** 类型代码：G / D / K */
  type_code: string;
  /** 出发站 */
  departure: string;
  /** 到达站 */
  destination: string;
  /** 出发时间 HH:mm */
  departure_time: string;
  /** 到达时间 HH:mm */
  arrival_time: string;
  /** 运行时长 */
  duration: string;
  /** 天数 */
  day: number;
  /** 是否可购买 */
  can_buy: boolean;
  /** 二等座 */
  seat_ed: string;
  /** 一等座/硬卧 */
  seat_yd: string;
  /** 无座/商务座 */
  seat_wz: string;
  /** 软卧 */
  seat_yw: string;
  /** 硬座 */
  seat_yz: string;
};

/** 火车票查询 data 结构 */
export interface TrainQueryData {
  /** 出发地 */
  departure: string;
  /** 目的地 */
  destination: string;
  /** 查询日期 */
  date: string;
  /** 总车次数量 */
  total: number;
  /** 车次列表 */
  trains: TrainItem[];
}

/** 火车票 SSE 完整消息 */
export interface SseTrainMessage extends Omit<SseQueryMessage, "data"> {
  msg_type: "train";
  action: "search";
  data: TrainQueryData;
}

// ════════════════════════════════════════════════════
//  租车查询专用类型
// ════════════════════════════════════════════════════

/** 单辆租车信息 */
export type CarItem = {
  /** 品牌（如 哈弗、比亚迪） */
  brand: string;
  /** 车型全称（如 哈弗H9 2.0T 汽油四驱尊享型） */
  model: string;
  /** 车型组（SUV / 新能源 / 越野 / ...） */
  car_type: string;
  /** 座位数 */
  seats: number;
  /** AI推荐理由 */
  reason: string;
  /** 适用场景（如 川西自驾） */
  scenario: string;
};

/** 租车查询 data 结构 */
export interface CarQueryData {
  /** 取车城市 */
  city: string;
  /** 还车城市 */
  return_city: string;
  /** 总车辆数 */
  total: number;
  /** 取车日期 */
  pickup_date: string;
  /** 还车日期 */
  return_date: string;
  /** 适用场景/路线 */
  scenario: string;
  /** AI综合推荐文案 */
  ai_text: string;
  /** 车辆列表 */
  cars: CarItem[];
}

/** 租车 SSE 完整消息 */
export interface SseCarMessage extends Omit<SseQueryMessage, "data"> {
  msg_type: "car";
  action: "search";
  data: CarQueryData;
}

// ════════════════════════════════════════════════════
//  酒店查询专用类型
// ════════════════════════════════════════════════════

/** 单个酒店信息（字段与后端 /sse/history 返回的 data.hotels[] 一致） */
export type HotelSearchItem = {
  /** 酒店ID */
  id?: string | number;
  /** 酒店名称 */
  hotel_name: string;
  /** 封面图 URL */
  cover_image?: string;
  /** 星级文本（如 五星级 / 四星级 / 未知） */
  star_text?: string;
  /** 星级数值（0-5，0=未知） */
  star_rating?: number;
  /** 联系电话（可能含多个号码） */
  contact_phone?: string;
  /** 最低价（0 表示暂无房/无报价） */
  min_price?: number | string;
  /** 可订房型数（0 表示暂无房） */
  room_count?: number;
  /** 该时段是否有房 */
  has_available?: boolean;
  /** 酒店简介 */
  intro?: string;
  /** 详细地址 */
  address?: string;
  /** 所在城市 */
  city?: string;
  /** 所在区 */
  district?: string;
  /** 距离（米） */
  distance?: number;
  /** 纬度 */
  latitude?: string | number;
  /** 经度 */
  longitude?: string | number;
  /** 房型列表 */
  rooms?: {
    name?: string;
    price?: string | number;
    breakfast?: string;
    cancel_policy?: string;
  }[];
  /** 兼容旧/备用字段名 */
  name?: string;
  price?: string | number;
  rating?: string | number;
  tel?: string;
  thumb?: string;
  tag?: string;
  photos?: string[];
  type?: string;
  reason?: string;
  [key: string]: any;
};

/** 酒店查询 data 结构 */
export interface HotelQueryData {
  /** 所在城市/查询中心（如 "成都四河地铁站附近"） */
  city?: string;
  /** 查询地点关键词（如 "成都四河地铁站"） */
  place?: string;
  /** 总酒店数 */
  total?: number;
  /** 入住日期 */
  check_in?: string;
  /** 退房日期 */
  check_out?: string;
  /** 酒店列表 */
  hotels: HotelSearchItem[];
}

/** 酒店 SSE 完整消息 */
export interface SseHotelMessage extends Omit<SseQueryMessage, "data"> {
  msg_type: "hotel";
  action: "search";
  data: HotelQueryData;
}

// ════════════════════════════════════════════════════
//  美食/餐厅查询专用类型
// ════════════════════════════════════════════════════

/** 单个餐厅信息 */
export type FoodItem = {
  /** 餐厅ID */
  id: string;
  /** 餐厅名称 */
  name: string;
  /** 详细地址 */
  address: string;
  /** 所在城市 */
  city: string;
  /** 所在区 */
  district: string;
  /** 经纬度 */
  lng: string;
  /** 距离（米） */
  distance: number;
  /** 类型分类（如 餐饮服务;中餐厅;火锅店） */
  type: string;
  /** 电话 */
  tel: string;
  /** 特色菜品标签（逗号分隔字符串） */
  tag: string;
  /** 评分（如 4.7） */
  rating: number;
  /** 图片列表 */
  photos: string[];
};

/** 美食查询 data 结构 */
export interface FoodQueryData {
  /** 所在城市 */
  city: string;
  /** 总餐厅数 */
  total: number;
  /** 餐厅列表 */
  foods: FoodItem[];
}

/** 美食 SSE 完整消息 */
export interface SseFoodMessage extends Omit<SseQueryMessage, "data"> {
  msg_type: "food";
  action: "search";
  data: FoodQueryData;
}

// ════════════════════════════════════════════════════
//  导游查询专用类型
// ════════════════════════════════════════════════════

/** 单个导游信息 */
export type GuideItem = {
  /** 导游ID */
  id: number;
  /** 姓名 */
  name: string;
  /** 称号/标题 */
  title: string;
  /** 头像URL */
  thumb: string;
  /** 性别（男/女） */
  sex: string;
  /** 所在地点/酒店 */
  place: string;
  /** 关系类型（如 平台自营） */
  relationship: string;
  /** 评分（字符串，如 "60.00"） */
  rating: string;
};

/** 导游查询 data 结构 */
export interface GuideQueryData {
  /** 所在城市 */
  city: string;
  /** 总导游数 */
  total: number;
  /** 导游列表 */
  guides: GuideItem[];
}

/** 导游 SSE 完整消息 */
export interface SseGuideMessage extends Omit<SseQueryMessage, "data"> {
  msg_type: "guide";
  action: "search";
  data: GuideQueryData;
}

// ════════════════════════════════════════════════════
//  机票/航班查询专用类型
// ════════════════════════════════════════════════════

/** 单个航班信息 */
export type FlightItem = {
  /** 航班名称（如 中国国际航空CA4119） */
  name: string;
  /** 机型（如 空客320(中)） */
  type: string;
  /** 起飞时间（如 07:00） */
  sarttime: string;
  /** 出发机场航站楼（如 双流机场T2） */
  startaddr: string;
  /** 飞行时长（如 2h40m） */
  times: string;
  /** 到达时间（如 09:40） */
  endtime: string;
  /** 到达机场航站楼（如 首都机场T3） */
  endaddr: string;
  /** 标签列表（如有早餐、热早餐等） */
  labels: string[];
};

/** 机票查询 data 结构 */
export interface FlightQueryData {
  /** 出发城市 */
  departure: string;
  /** 目的城市 */
  destination: string;
  /** 出发日期时间（如 2026-05-31 08:00） */
  date: string;
  /** 去程航班总数 */
  total: number;
  /** 去程航班列表 */
  flights: FlightItem[];
  /** 是否往返票 */
  is_round_trip?: boolean;
  /** 返程航班总数 */
  return_total?: number;
  /** 返程日期时间 */
  return_date?: string;
  /** 返程航班列表 */
  return_flights?: FlightItem[];
}

/** 机票 SSE 完整消息 */
export interface SseFlightMessage extends Omit<SseQueryMessage, "data"> {
  msg_type: "flight";
  action: "search";
  data: FlightQueryData;
}

// ════════════════════════════════════════════════════
//  线路/行程规划查询专用类型
// ════════════════════════════════════════════════════

/** 单个日程项（时间轴） */
export type ScheduleItem = {
  /** 时间（如 08:00） */
  time: string;
  /** 活动描述 */
  activity: string;
  /** 备注 */
  note?: string;
};

/** 餐饮信息 */
export type MealItem = {
  /** 类型（午餐/晚餐） */
  type: string;
  /** 时间 */
  time: string;
  /** 推荐餐厅及说明 */
  suggest: string;
};

/** 住宿信息 */
export type HotelItem = {
  /** 酒店名称及描述 */
  name: string;
  /** 价格 */
  price?: string;
  /** 推荐理由 */
  reason?: string;
};

/** 单日行程计划 */
export type DayPlan = {
  /** 日期 */
  date: string;
  /** 当日主题 */
  theme: string;
  /** 当日驾驶信息 */
  driving_today: string;
  /** 日程安排 */
  schedule: ScheduleItem[];
  /** 餐饮推荐 */
  meals?: MealItem[];
  /** 住宿推荐 */
  hotel?: HotelItem;
  /** 温馨提示 */
  tips?: string;
  /** 第几天 */
  day: number;
};

/** 行程概要信息 */
export type ItineraryInfo = {
  overview: string;
  summary: string;
  daily_summary?: string;
  days: DayPlan[];
  route_info?: {
    path: string;
    total_km: number;
    total_hours: number;
    segments: string;
  };
};

/** 单条路线方案 */
export type RoutePlan = {
  name: string;
  overview: string;
  summary: string;
  departure: string;
  destination: string;
  days: number;
  total_km: number;
  total_hours: number;
  route_path: string[];
  daily_plans: DayPlan[];
};

/** 线路规划 data 结构 */
export interface RouteQueryData {
  /** 出发地 */
  departure: string;
  /** 目的地 */
  destination: string;
  /** 数据来源 */
  source?: string;
  /** 天数 */
  days: number;
  /** 概要文案 */
  message?: string;
  /** 总公里数 */
  total_km: number;
  /** 总小时数 */
  total_hours: number;
  /** 路线途经点 */
  route_path?: string[];
  /** 行程详情 */
  itinerary?: ItineraryInfo;
  /** 备选方案列表 */
  routes?: RoutePlan[];
}

/** 线路规划 SSE 完整消息 */
export interface SseRouteMessage extends Omit<SseQueryMessage, "data"> {
  msg_type: "route";
  action: "search";
  data: RouteQueryData;
}

// ════════════════════════════════════════════════════
//  接送服务查询专用类型
// ════════════════════════════════════════════════════

/** 单个接送方案 */
export type TransferPlanItem = {
  /** 方案名称（如 "精准接站经济线"、"尊享接站品质线"） */
  plan_name: string;
  /** 方案类型：economy(经济) / premium(品质) */
  plan_type: string;
  /** 车辆类型（如 B级轿车、豪华轿车） */
  vehicle_type: string;
  /** 具体车型（如 丰田凯美瑞双擎、比亚迪汉EV创世版） */
  vehicle_model: string;
  /** 核载人数 */
  capacity: number;
  /** 亮点列表 */
  highlights: string[];
  /** 适用人群描述 */
  suitable_for: string;
  /** 温馨提示 */
  tips: string;
  /** 距离(km) */
  distance_km: number;
  /** 预计时长(分钟) */
  duration_min: number;
  /** 时长文本（如 "27分钟"） */
  duration_text: string;
};

/** 接送查询 data 结构 */
export interface TransferQueryData {
  /** 出发地（如 北京西站） */
  departure: string;
  /** 目的地（如 天安门） */
  destination: string;
  /** 用车时间（如 2026-06-07 15:30） */
  datetime: string;
  /** 乘客人数 */
  passengers: number;
  /** 场景：接站 / 送站 / 其他 */
  scene: string;
  /** 预算 */
  budget: string | null;
  /** 品牌（可选） */
  brand: string | null;
  /** 车型偏好（可选） */
  car_model: string | null;
  /** 总距离(km) */
  distance_km: number;
  /** 总时长(分钟) */
  duration_min: number;
  /** 时长文本 */
  duration_text: string;
  /** 方案列表 */
  transfers: TransferPlanItem[];
}

/** 接送 SSE 完整消息 */
export interface SseTransferMessage extends Omit<SseQueryMessage, "data"> {
  msg_type: "transfer";
  action: "search";
  data: TransferQueryData;
}

// ════════════════════════════════════════════════════
//  业务预定订单消息专用类型
// ════════════════════════════════════════════════════

/** 订单中的商品项 */
export type OrderGoodsItem = {
  name: string;
  number: number;
};

/** 预定订单 data 结构（__data 字段） */
export interface BookingOrderData {
  /** 任务参数（原始需求） */
  task_params: Record<string, any>;
  /** 订单号 */
  order_no: string;
  /** 订单金额 */
  order_amount: number;
  /** 商品数量 */
  goods_count: number;
  /** 商品列表 */
  goods: OrderGoodsItem[];
}

/** 预定订单 SSE 完整消息 */
export interface SseBookingOrderMessage extends Omit<SseQueryMessage, "data"> {
  msg_type: "order" | "booking";
  action: "book";
  data?: any; // 原始 data 可能为空，实际数据在 __data 中
}

/** SSE消息列表项类型（用于前端展示） */
export interface SseMessageListItem {
  id: string;          // 唯一标识，如 "status" / "connect" / "greeting"
  label: string;       // 显示名称：如 "服务状态" / "连接信息" / "打招呼"
  icon: string;        // 图标 class
  apiPath: string;     // 接口路径：如 "/sse/status"
  method: "GET";       // 请求方法
  description: string; // 描述说明
  category: string;    // 分类：system / message / query / navigation
}

/** 所有接收SSE消息的接口列表 */
export const SSE_MESSAGE_LIST: SseMessageListItem[] = [
  {
    id: "connect",
    label: "连接上SSE服务信息",
    icon: "ri-link",
    apiPath: "/sse/connect",
    method: "GET",
    description: "获取当前SSE连接的身份信息和会话ID",
    category: "system",
  },
  {
    id: "status",
    label: "SSE服务状态信息",
    icon: "ri-server-line",
    apiPath: "/sse/status",
    method: "GET",
    description: "查看SSE服务运行状态、连接数、AI模型信息",
    category: "system",
  },
  {
    id: "greeting",
    label: "上线打招呼信息",
    icon: "ri-hand-heart-line",
    apiPath: "/sse/greeting",
    method: "GET",
    description: "获取AI助手欢迎语",
    category: "message",
  },
  {
    id: "normal",
    label: "普通消息",
    icon: "ri-chat-1-line",
    apiPath: "/sse/message",
    method: "GET",
    description: "获取普通AI聊天回复消息",
    category: "message",
  },
  {
    id: "tts",
    label: "文字转语音消息",
    icon: "ri-volume-up-line",
    apiPath: "/sse/tts",
    method: "GET",
    description: "获取AI回复的TTS语音播报消息（含音频URL）",
    category: "message",
  },
  {
    id: "train",
    label: "火车票查询消息",
    icon: "ri-train-line",
    apiPath: "/sse/train",
    method: "GET",
    description: "查询火车票相关信息",
    category: "query",
  },
  {
    id: "car",
    label: "租车查询信息",
    icon: "ri-car-line",
    apiPath: "/sse/car",
    method: "GET",
    description: "查询租车相关信息",
    category: "query",
  },
  {
    id: "hotel",
    label: "酒店查询信息",
    icon: "ri-hotel-line",
    apiPath: "/sse/hotel",
    method: "GET",
    description: "查询酒店推荐信息",
    category: "query",
  },
  {
    id: "food",
    label: "美食查询信息",
    icon: "ri-resturant-line",
    apiPath: "/sse/food",
    method: "GET",
    description: "查询美食推荐信息",
    category: "query",
  },
  {
    id: "guide",
    label: "导游查询",
    icon: "ri-user-star-line",
    apiPath: "/sse/guide",
    method: "GET",
    description: "查询导游相关信息",
    category: "query",
  },
  {
    id: "flight",
    label: "飞机票查询",
    icon: "ri-plane-line",
    apiPath: "/sse/flight",
    method: "GET",
    description: "查询飞机票信息",
    category: "query",
  },
  {
    id: "navigate",
    label: "跳转页面",
    icon: "ri-direction-line",
    apiPath: "/sse/navigate",
    method: "GET",
    description: "获取页面跳转指令",
    category: "navigation",
  },
];

// ════════════════════════════════════════════════════
//  API 函数
// ════════════════════════════════════════════════════

/**
 * 发送AI聊天消息
 * POST /sse/send
 *
 * @param params - 发送参数，包含消息类型和内容
 * @returns 返回 { success, message_id, hint }
 *          AI回复将通过SSE长连接异步推送
 */
export async function sendAIChatMessage(
  params: SendAIChatMessageParams,
): Promise<SendAIChatMessageResponse> {
  // 文本消息用 JSON，语音消息（File/Blob）用 FormData
  const isFile = params.content instanceof File || params.content instanceof Blob;
  let body: FormData | Record<string, unknown>;

  if (isFile) {
    // 音频/文件：FormData，浏览器自动设置含 boundary 的 Content-Type
    const form = new FormData();
    form.append("msg_type", params.type);
    form.append("content", params.content as File | Blob);
    if (params.session_id) {
      form.append("session_id", params.session_id);
    }
    body = form;
  } else {
    // 文本消息：JSON 格式（字段名 msg_type 对应后端接口要求）
    body = { msg_type: params.type, content: params.content };
    if (params.session_id) (body as any).session_id = params.session_id;
  }

  // 音频消息需要更长超时（服务端 STT 转写耗时），文本用默认 15s
  const res = await sseHttp.post<SendAIChatMessageResponse>("/sse/send", body, {
    timeout: isFile ? 30000 : 15000,
  });

  const payload = res.data?.data ?? res.data;
  if (!payload?.success) {
    throw new Error((payload as any).message || (payload as any).hint || "发送AI消息失败");
  }

  return payload;
}

/**
 * 绑定AI聊天会话到当前登录账号
 * POST /sse/body
 *
 * 将游客会话合并到登录用户下
 * token / Chat-Token 通过 axios 拦截器自动注入
 *
 * @param options - 合并策略（可选）
 * @returns 返回绑定结果及新身份信息
 */
export async function bindAIChatSession(
  options: BindAIChatSessionParams = {},
): Promise<BindAIChatSessionResponse> {
  const res = await sseHttp.post<BindAIChatSessionResponse>(
    "/sse/body",
    { merge_strategy: options.merge_strategy ?? "append" },
  );

  const payload = res.data?.data ?? res.data;
  if (!payload?.success) {
    throw new Error(payload.message || "绑定聊天会话失败");
  }

  return payload;
}

/**
 * 获取AI聊天历史消息列表
 * GET /sse/history?limit=20&before=xxx&page=1
 *
 * @param params - 查询参数
 * @returns 返回消息列表及游标分页信息
 */
export async function fetchAIChatMessages(
  params: FetchAIChatMessagesParams = {},
): Promise<FetchAIChatMessagesResponse["data"] & { messages: HistoryMessage[] }> {
  const res = await sseHttp.get<{ data: FetchAIChatMessagesResponse }>("/sse/history", {
    params: {
      limit: params.limit ?? 20,
      before: params.before ?? Math.floor(Date.now()),
      page: params.page ?? 1,
    },
  });

  // 兼容两种响应格式：{ data: { success, ... } } 或直接 { success, ... }
  const data = res.data?.data ?? res.data;

  if (!data?.success) {
    throw new Error("获取AI消息列表失败");
  }

  return data as any;
}

// ════════════════════════════════════════════════════
//  接收SSE消息相关 API 函数
// ════════════════════════════════════════════════════

/**
 * 获取SSE服务状态信息
 * GET /sse/status
 */
export async function fetchSseStatus(): Promise<SseStatusInfo> {
  const res = await sseHttp.get<any>("/sse/status");
  const data = res.data?.data ?? res.data;
  return data;
}

/**
 * 连接上SSE服务，获取身份信息
 * GET /sse/connect
 * Header: Chat-Token, Chat-Session（由拦截器自动注入）
 */
export async function fetchSseConnect(): Promise<SseConnectInfo> {
  const res = await sseHttp.get<any>("/sse/connect");
  const data = res.data?.data ?? res.data;
  return data;
}

/**
 * 获取上线打招呼信息
 * GET /sse/greeting
 */
export async function fetchSseGreeting(): Promise<SseGreetingInfo> {
  const res = await sseHttp.get<any>("/sse/greeting");
  const data = res.data?.data ?? res.data;
  return data;
}

/**
 * 获取普通消息
 * GET /sse/message
 */
export async function fetchSseNormalMessage(): Promise<SseNormalMessage> {
  const res = await sseHttp.get<any>("/sse/message");
  const data = res.data?.data ?? res.data;
  return data;
}

/**
 * 获取TTS文字转语音消息
 * GET /sse/tts
 */
export async function fetchSseTtsMessage(): Promise<SseTtsMessage> {
  const res = await sseHttp.get<any>("/sse/tts");
  const data = res.data?.data ?? res.data;
  return data;
}

/**
 * 获取火车票查询消息
 * GET /sse/train
 */
export async function fetchSseTrainMessage(): Promise<SseTrainMessage> {
  const res = await sseHttp.get<any>("/sse/train");
  const data = res.data?.data ?? res.data;
  return data;
}

/**
 * 获取租车查询信息
 * GET /sse/car
 */
export async function fetchSseCarMessage(): Promise<SseQueryMessage> {
  const res = await sseHttp.get<any>("/sse/car");
  const data = res.data?.data ?? res.data;
  return data;
}

/**
 * 获取美食查询信息
 * GET /sse/food
 */
export async function fetchSseFoodMessage(): Promise<SseQueryMessage> {
  const res = await sseHttp.get<any>("/sse/food");
  const data = res.data?.data ?? res.data;
  return data;
}

/**
 * 获取导游查询信息
 * GET /sse/guide
 */
export async function fetchSseGuideMessage(): Promise<SseQueryMessage> {
  const res = await sseHttp.get<any>("/sse/guide");
  const data = res.data?.data ?? res.data;
  return data;
}

/**
 * 获取飞机票查询信息
 * GET /sse/flight
 */
export async function fetchSseFlightMessage(): Promise<SseQueryMessage> {
  const res = await sseHttp.get<any>("/sse/flight");
  const data = res.data?.data ?? res.data;
  return data;
}

/**
 * 获取跳转页面指令
 * GET /sse/navigate
 */
export async function fetchSseNavigateMessage(): Promise<SseQueryMessage> {
  const res = await sseHttp.get<any>("/sse/navigate");
  const data = res.data?.data ?? res.data;
  return data;
}

/** 根据接口ID调用对应的SSE消息接口 */
export const SSE_API_MAP: Record<string, () => Promise<any>> = {
  status: fetchSseStatus,
  connect: fetchSseConnect,
  greeting: fetchSseGreeting,
  normal: fetchSseNormalMessage,
  tts: fetchSseTtsMessage,
  train: fetchSseTrainMessage,
  car: fetchSseCarMessage,
  food: fetchSseFoodMessage,
  guide: fetchSseGuideMessage,
  flight: fetchSseFlightMessage,
  navigate: fetchSseNavigateMessage,
};
